import { ease1 } from '@/animations/easings';
import { fetchSwiper } from '@/dynamic-modules';
import { createCounter, createNav, destroyNav } from '@/inits/components/common/custom-reviews-slider';
import PageHandlersService from '@/utils/page-handlers-service';
import { DEVICE_WIDTH } from '@/utils/viewport-width';
import type { Swiper as SwiperType } from 'swiper';
import gsap from 'gsap';

const pageHandlers = new PageHandlersService();

const init = async (container: HTMLElement | Document = document) => {
    const videoContainers = document.querySelectorAll('.js-video-visual-part');
    const cursor = document.querySelector('.cursor');

    videoContainers.forEach((container) => {
        const videoPopup = container.querySelector('.js-video-popup');
        const frameContainer = videoPopup?.querySelector('.js-video-popup-frame-container');
        const videoFrame = frameContainer?.querySelector('iframe');
        const closeButtons = container.querySelectorAll('.js-video-popup-close-btn');
        const videoPlayButton = container.querySelector('.js-video-play-btn');

        videoFrame?.classList.add('video-popup__iframe');
        if (videoFrame) {
            videoFrame.dataset.src = videoFrame.src;
        }

        let videoSrcResetTimeout: NodeJS.Timeout;
        let isFirstTimeOpening = true;

        const handleWatchButtonClick = () => {
            videoPopup?.classList.add('active');
            cursor?.classList.add('cursor--force-hidden');

            if (videoSrcResetTimeout) {
                clearTimeout(videoSrcResetTimeout);
            }

            if (isFirstTimeOpening) {
                videoFrame?.setAttribute('src', videoFrame.dataset.src ?? '');
                isFirstTimeOpening = false;
            }
        };

        const handleCloseButtonClick = () => {
            videoPopup?.classList.remove('active');

            videoSrcResetTimeout = setTimeout(() => {
                videoFrame?.setAttribute('src', videoFrame.src);
                cursor?.classList.remove('cursor--force-hidden');
            }, 600);
        };

        pageHandlers.set(videoPlayButton, handleWatchButtonClick);

        closeButtons.forEach((closeButton) => {
            pageHandlers.set(closeButton, handleCloseButtonClick);
        });
    });

    const accordionsGroups = document.querySelectorAll('.js-pr-accordions-group');

    accordionsGroups.forEach((accordionsGroup) => {
        const accordions = accordionsGroup.querySelectorAll('.js-pr-accordion');

        accordions.forEach((accordion) => {
            const label = accordion.querySelector('.js-pr-accordion-label');
            const collapse = accordion.querySelector('.js-pr-accordion-collapse');

            if (label && collapse) {
                const handleLabelClick = () => {
                    if (accordion.classList.contains('active')) {
                        gsap.set(collapse, { height: 0 });

                        accordion.classList.remove('active');
                    } else {
                        accordions.forEach((accordion) => {
                            accordion.classList.remove('active');
                            const collapse = accordion.querySelector('.js-pr-accordion-collapse');

                            gsap.set(collapse, { height: 0 });
                        });

                        accordion.classList.add('active');

                        gsap.set(collapse, { height: collapse.scrollHeight });
                    }
                };

                pageHandlers.set(label, handleLabelClick);
            }
        });
    });

    const reviewsSlider = container.querySelector<HTMLElement>('.js-reviews-slider');

    if (reviewsSlider) {
        const slides = Array.from(reviewsSlider.querySelectorAll<HTMLElement>('.swiper-slide'));

        if (slides.length > 1) {
            const { Swiper, Lazy } = await fetchSwiper();
            const state = { isAnimating: false };

            const changeSlide = (swiper: SwiperType) => {
                const isNextDirection =
                    (swiper.previousIndex < swiper.activeIndex ||
                        (swiper.previousIndex === slides.length - 1 && swiper.activeIndex === 0)) &&
                    !(swiper.previousIndex === 0 && swiper.activeIndex === slides.length - 1);

                const currentImg = swiper.slides[swiper.previousIndex].querySelector<HTMLElement>(
                    '.js-reviews-slider-slide__img-wrapper',
                );
                const currentTitle = swiper.slides[swiper.previousIndex].querySelector<HTMLElement>(
                    '.js-reviews-slider-slide__title',
                );
                const currentAuthor = swiper.slides[swiper.previousIndex].querySelector<HTMLElement>(
                    '.js-reviews-slider-slide__author',
                );
                const currentBtn = swiper.slides[swiper.previousIndex].querySelector<HTMLElement>(
                    '.js-reviews-slider-slide__btn',
                );

                const nextImg = swiper.slides[swiper.activeIndex].querySelector<HTMLElement>(
                    '.js-reviews-slider-slide__img-wrapper',
                );
                const nextTitle = swiper.slides[swiper.activeIndex].querySelector<HTMLElement>(
                    '.js-reviews-slider-slide__title',
                );
                const nextAuthor = swiper.slides[swiper.activeIndex].querySelector<HTMLElement>(
                    '.js-reviews-slider-slide__author',
                );
                const nextBtn = swiper.slides[swiper.activeIndex].querySelector<HTMLElement>(
                    '.js-reviews-slider-slide__btn',
                );

                const tl = gsap.timeline({
                    defaults: { ease: ease1 },
                    onStart: () => {
                        state.isAnimating = true;
                        swiper.allowTouchMove = false;
                    },
                    onComplete: () => {
                        state.isAnimating = false;

                        if (!DEVICE_WIDTH.isDesktop) {
                            swiper.allowTouchMove = true;
                        }
                    },
                });

                if (!DEVICE_WIDTH.isDesktop) {
                    tl.fromTo(
                        [currentTitle, currentAuthor, currentBtn],
                        { y: 0, opacity: 1 },
                        {
                            duration: 0.6,
                            y: 15,
                            opacity: 0,
                            stagger: 0.1,
                        },
                    )
                        .fromTo(
                            currentImg,
                            { xPercent: 0 },
                            { xPercent: isNextDirection ? -100 : 100, duration: 1, ease: 'power3.inOut' },
                            '-=0.3',
                        )
                        .fromTo(
                            nextImg,
                            { xPercent: isNextDirection ? 100 : -100 },
                            { xPercent: 0, duration: 1, ease: 'power3.inOut' },
                            '-=1',
                        )
                        .fromTo(
                            [nextTitle, nextAuthor, nextBtn],
                            { y: 15, opacity: 0 },
                            {
                                duration: 0.6,
                                y: 0,
                                opacity: 1,
                                stagger: 0.1,
                            },
                        );
                } else {
                    tl.fromTo(
                        [currentTitle, currentAuthor, currentBtn],
                        { y: 0, opacity: 1 },
                        {
                            duration: 0.6,
                            y: 15,
                            opacity: 0,
                            stagger: 0.1,
                        },
                    )
                        .fromTo(
                            currentImg,
                            { xPercent: 0 },
                            {
                                xPercent: isNextDirection ? -100 : 100,
                                duration: 1,
                                ease: 'power3.inOut',
                                onComplete: () => {
                                    gsap.set(currentImg, {
                                        opacity: 0,
                                        pointerEvents: 'none',
                                    });
                                },
                            },
                            '-=0.9',
                        )
                        .fromTo(
                            nextImg,
                            { xPercent: isNextDirection ? 100 : -100, opacity: 1, pointerEvents: 'all' },
                            { xPercent: 0, duration: 1, ease: 'power3.inOut' },
                            '-=1',
                        )
                        .fromTo(
                            [nextTitle, nextAuthor, nextBtn],
                            { y: 15, opacity: 0 },
                            {
                                duration: 0.6,
                                y: 0,
                                opacity: 1,
                                stagger: 0.1,
                            },
                        );
                }
            };

            const slider = new Swiper(reviewsSlider, {
                modules: [Lazy /* Autoplay */],
                allowTouchMove: !DEVICE_WIDTH.isDesktop,
                slidesPerView: 1,
                watchSlidesProgress: true,
                virtualTranslate: true,
                speed: 0,
                preloadImages: false,
                lazy: {
                    loadPrevNext: true,
                    checkInView: true,
                },
                rewind: true,
                on: {
                    init: (swiper) => {
                        if (
                            typeof swiper.params.slidesPerView === 'number' &&
                            slides.length <= swiper.params.slidesPerView
                        ) {
                            swiper.disable();
                        }

                        slides
                            .filter((_, i) => i !== swiper.activeIndex)
                            .forEach((el) => {
                                gsap.set(el.querySelector('.js-reviews-slider-slide__img-wrapper'), {
                                    xPercent: 100,
                                });
                            });
                    },
                    afterInit: (swiper) => {
                        const sliderNavs = Array.from(reviewsSlider.querySelectorAll<HTMLElement>('.js-slider-nav'));
                        const counters = Array.from(reviewsSlider.querySelectorAll<HTMLElement>('.js-slider-counter'));

                        sliderNavs.forEach((sliderNav) => {
                            createNav(swiper, state, sliderNav);
                        });

                        counters.forEach((counter) => {
                            createCounter(swiper, counter);
                        });
                    },
                    slideChange: (swiper) => {
                        requestAnimationFrame(() => changeSlide(swiper));
                    },
                    destroy: () => {
                        destroyNav(reviewsSlider);
                    },
                    resize: (swiper) => {
                        if (
                            typeof swiper.params.slidesPerView === 'number' &&
                            slides.length <= swiper.params.slidesPerView
                        ) {
                            swiper.disable();
                        } else {
                            swiper.enable();
                        }
                    },
                    slideChangeTransitionStart: (swiper) => {
                        if (!DEVICE_WIDTH.isDesktop) {
                            swiper.allowTouchMove = false;
                        }
                    },
                },
            });

            pageHandlers.addDestroy(() => {
                slider.destroy();
            });
        }

        if (DEVICE_WIDTH.isDesktop) {
            const reviewsSliderButtons = document.querySelectorAll('.js-reviews-slider-slide__btn');

            reviewsSliderButtons.forEach((button) => {
                button.classList.remove('btn-light');
                button.classList.add('btn-dark');
            });
        }
    }
};

const destroy = () => {
    pageHandlers.clear();
};

const _module = { init, destroy };

export default _module;
